import { Globe, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGUAGES = [
  { code: "tr", label: "Türkçe", short: "TR" },
  { code: "en", label: "English", short: "EN" },
] as const;

interface LanguageSwitcherProps {
  /** Compact variant for mobile header */
  compact?: boolean;
  className?: string;
}

const LanguageSwitcher = ({ compact = false, className = "" }: LanguageSwitcherProps) => {
  const { t, i18n } = useTranslation();
  const current = i18n.language?.toLowerCase().startsWith("tr") ? "tr" : "en";
  const active = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[1];

  const change = (code: string) => {
    if (code !== current) i18n.changeLanguage(code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("nav.language")}
          className={`inline-flex items-center gap-1.5 font-semibold tracking-wider text-muted-foreground hover:text-primary transition-colors rounded border border-border/40 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
            compact ? "h-8 px-2 text-xs" : "h-9 px-2 text-[11px]"
          } ${className}`}
        >
          <Globe size={compact ? 13 : 14} />
          <span>{active.short}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44 bg-popover border-border z-[60]">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground font-normal">
          {t("nav.language")}
        </DropdownMenuLabel>
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => change(lang.code)}
            className="cursor-pointer text-sm flex items-center justify-between"
          >
            <span className={lang.code === current ? "text-primary font-medium" : ""}>
              {lang.label}
            </span>
            {lang.code === current && <Check size={14} className="text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
